import type { ResizeEnable } from 'react-rnd';
import { CadNextBootstrapView, WorkspacePanelDto } from '@mark43/cad-api';
import { COMPACT_CLI_HEIGHT } from '../command-line/use-open-command-line-panel';
import {
    SENSOR_PANEL,
    SENSOR_EVENT_PANEL,
    SENSOR_GRID_PANEL,
} from '../sensors/client-only-panel-types';
import { SENSOR_GRID_PANEL_WIDTH } from '../sensors/sensor-grid-layout';

export const minimumDimensionsByPanelType: Partial<
    Record<WorkspacePanelDto['workspacePanelType'], [number, number]>
> = {
    BOLO: [500, 700],
    EVENT: [500, 700],
    SCHEDULED_EVENT: [500, 700],
    MEDIA_ATTACHMENTS: [500, 700],
    EXPUNGEMENTS: [640, 480],
    QUEUE: [253, 130],
    QUEUE_FORM: [1076, 525],
    MESSAGING: [700, 600],
    CONTACT_LIST: [670, 480],
    COMMAND_LINE: [800, COMPACT_CLI_HEIGHT],
    UNIT_HISTORY: [750, 600],
    DATA_EXCHANGE_FORMS: [240, 370],
    DATA_EXCHANGE_FORM: [500, 500],
    DATA_EXCHANGE_RESULTS: [800, 600],
    // No height floor for the content-sized panels (`heightFromContent`): they are as tall as
    // their content.
    [SENSOR_PANEL]: [460, 0],
    [SENSOR_EVENT_PANEL]: [460, 420],
    [SENSOR_GRID_PANEL]: [900, 0],
};

for (const [, val] of Object.entries(minimumDimensionsByPanelType)) {
    const minHeight = val[1];
    if (minHeight > 700) {
        // Our minimum screen size specs for cad are 1024x768. On a monitor with 768 vertical pixels, assuming
        // some vertical space dedicated to a windows task bar, some height dedicated to the cad's application header,
        // in app header, and in app footer, we determine that a panel with a minimum height of >700px cannot function well
        // in our minimum supported screen resolution
        throw new Error('Panels cannot have a minimum height of more than 700px');
    }
}

/**
 * Every handle off, for panels whose contents are laid out for one size. `Panel` types its
 * `resizeOptions` as `ResizeEnable`, so this is an object rather than a bare `false`.
 */
export const RESIZE_DISABLED: ResizeEnable = {
    top: false,
    right: false,
    bottom: false,
    left: false,
    topRight: false,
    bottomRight: false,
    bottomLeft: false,
    topLeft: false,
};

export const DEFAULT_EVENT_PANEL_HEIGHT = 889;

// This does not include events because we need to respect eventPanelCompactByDefault
// when opening new panels, which requires access to enabledFeatures.
export const defaultDimensionsByPanelType: Partial<
    Record<WorkspacePanelDto['workspacePanelType'], [number, number]>
> = {
    EXPUNGEMENTS: [800, 600],
    MESSAGING: [700, 600],
    CONTACT_LIST: [670, 480],
    COMMAND_LINE: [1024, COMPACT_CLI_HEIGHT],
    UNIT_HISTORY: [750, 600],
    DATA_EXCHANGE_FORMS: [240, 364],
    DATA_EXCHANGE_FORM: [500, 650],
    DATA_EXCHANGE_RESULTS: [992, 745],
    // The sensor and sensor grid panels are as tall as their content; their heights here only
    // place a new panel before it has rendered.
    [SENSOR_PANEL]: [680, 820],
    [SENSOR_EVENT_PANEL]: [640, 520],
    [SENSOR_GRID_PANEL]: [SENSOR_GRID_PANEL_WIDTH, 660],
};

export const enabledFeatureForViewByPanelType: Partial<
    Record<WorkspacePanelDto['workspacePanelType'], keyof CadNextBootstrapView['enabledFeatures']>
> = {
    MESSAGING: 'messaging',
    CONTACT_LIST: 'viewContactList',
    DATA_EXCHANGE_FORMS: 'dataExchange',
    DATA_EXCHANGE_FORM: 'dataExchange',
    DATA_EXCHANGE_RESULTS: 'dataExchange',
    UNIT_HISTORY: 'unitHistory',
    BOLO: 'viewBolos',
    MEDIA_ATTACHMENTS: 'mediaAttachmentsEnabled',
    [SENSOR_PANEL]: 'sensorsEnabled',
    [SENSOR_EVENT_PANEL]: 'sensorsEnabled',
    [SENSOR_GRID_PANEL]: 'sensorsEnabled',
};
